/**
 * wt-reputation (Onda 3C) — PublicRating
 *
 * Public page served at /rate/:token — no auth required, no Sidebar.
 * Early-return in App.tsx renders this component when
 * location.pathname.startsWith('/rate/').
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const CATEGORIES = [
  { key: 'atendimento', labelKey: 'publicRating.category.service' },
  { key: 'pontualidade', labelKey: 'publicRating.category.punctuality' },
  { key: 'ambiente', labelKey: 'publicRating.category.environment' },
];

interface RatingContext {
  alreadySubmitted: boolean;
  clinicName: string;
  professionalName?: string;
  appointmentDate?: string;
  procedureNames?: string[];
  categories?: string[];
}

function StarRow({
  value,
  onChange,
  size = 32,
  readonly = false,
}: {
  value: number | null;
  onChange?: (v: number) => void;
  size?: number;
  readonly?: boolean;
}) {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {[1, 2, 3, 4, 5].map((star) => {
        const active = hovered != null ? star <= hovered : value != null && star <= value;
        return (
          <span
            key={star}
            onClick={() => !readonly && onChange && onChange(star)}
            onMouseEnter={() => !readonly && setHovered(star)}
            onMouseLeave={() => !readonly && setHovered(null)}
            role={readonly ? undefined : 'button'}
            aria-label={t('publicRating.starsAria', { count: star })}
            style={{
              fontSize: size,
              lineHeight: 1,
              cursor: readonly ? 'default' : 'pointer',
              color: active ? 'var(--warning)' : 'var(--line)',
              transition: 'color 0.1s',
              userSelect: 'none',
            }}
          >
            ★
          </span>
        );
      })}
    </div>
  );
}

export default function PublicRating() {
  const { t } = useTranslation();
  const token = window.location.pathname.replace('/rate/', '').split('/')[0];

  const [ctx, setCtx] = useState<RatingContext | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [score, setScore] = useState<number | null>(null);
  const [feedback, setFeedback] = useState('');
  const [catScores, setCatScores] = useState<Record<string, number>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [googleReviewUrl, setGoogleReviewUrl] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    fetch(`/api/reputation/rate/${token}`)
      .then((r) => r.json())
      .then((data) => {
        if (!data.success) {
          setLoadError(t('publicRating.linkNotFound'));
          return;
        }
        setCtx(data);
        if (data.alreadySubmitted) setSubmitted(true);
      })
      .catch(() => setLoadError(t('publicRating.loadError')));
  }, [token, t]);

  const handleSubmit = async () => {
    if (score == null) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch(`/api/reputation/rate/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ score, feedback, categories: catScores }),
      });
      const data = await res.json();
      if (!data.success) {
        setSubmitError(data.error || t('publicRating.submitError'));
        return;
      }
      setSubmitted(true);
      if (data.googleReviewUrl) setGoogleReviewUrl(data.googleReviewUrl);
    } catch {
      setSubmitError(t('publicRating.networkError'));
    } finally {
      setSubmitting(false);
    }
  };

  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    background: '#f9fafb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    padding: '24px 16px',
  };

  const cardStyle: React.CSSProperties = {
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
    maxWidth: 480,
    width: '100%',
    padding: '32px 28px',
  };

  if (!token || loadError) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <p style={{ textAlign: 'center', color: '#6b7280', fontSize: 16 }}>
            {loadError || t('publicRating.invalidLink')}
          </p>
        </div>
      </div>
    );
  }

  if (!ctx) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <p style={{ textAlign: 'center', color: '#6b7280' }}>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={containerStyle}>
        <div style={{ ...cardStyle, textAlign: 'center' }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>🎉</div>
          <h2 style={{ margin: '0 0 8px', fontSize: 22, color: '#111' }}>
            {t('publicRating.thankYouTitle')}
          </h2>
          <p style={{ color: '#6b7280', margin: '0 0 24px' }}>
            {t('publicRating.thankYouBody', { clinic: ctx.clinicName || t('publicRating.us') })}
          </p>
          {googleReviewUrl && (
            <a
              href={googleReviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                background: '#4285f4',
                color: '#fff',
                padding: '12px 24px',
                borderRadius: 8,
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: 15,
              }}
            >
              {t('publicRating.leaveGoogleReview')}
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        {/* Header */}
        <div style={{ marginBottom: 24, textAlign: 'center' }}>
          {ctx.clinicName && (
            <p style={{ margin: '0 0 4px', color: '#6b7280', fontSize: 13, fontWeight: 500 }}>
              {ctx.clinicName}
            </p>
          )}
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#111' }}>
            {t('publicRating.heading')}
          </h1>
          {ctx.professionalName && (
            <p style={{ margin: '6px 0 0', color: '#6b7280', fontSize: 14 }}>
              {t('publicRating.withProfessional', { name: ctx.professionalName })}
            </p>
          )}
          {ctx.procedureNames && ctx.procedureNames.length > 0 && (
            <p style={{ margin: '4px 0 0', color: '#9ca3af', fontSize: 13 }}>
              {ctx.procedureNames.join(', ')}
            </p>
          )}
        </div>

        {/* Overall star rating */}
        <div style={{ marginBottom: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#374151' }}>
            {t('publicRating.overallRating')}
          </p>
          <StarRow value={score} onChange={setScore} size={44} />
          {score != null && (
            <p style={{ margin: 0, fontSize: 13, color: '#9ca3af' }}>
              {score === 1 ? t('publicRating.score.veryBad') : score === 2 ? t('publicRating.score.bad') : score === 3 ? t('publicRating.score.regular') : score === 4 ? t('publicRating.score.good') : t('publicRating.score.excellent')}
            </p>
          )}
        </div>

        {/* Category ratings */}
        <div style={{ marginBottom: 24 }}>
          {CATEGORIES.map((cat) => (
            <div key={cat.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>{t(cat.labelKey)}</span>
              <StarRow
                value={catScores[cat.key] ?? null}
                onChange={(v) => setCatScores((prev) => ({ ...prev, [cat.key]: v }))}
                size={24}
              />
            </div>
          ))}
        </div>

        {/* Feedback text */}
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
            {t('publicRating.commentLabel')}
          </label>
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder={t('publicRating.commentPlaceholder')}
            rows={3}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              border: '1px solid var(--line)',
              borderRadius: 8,
              padding: '10px 12px',
              fontSize: 14,
              color: '#111',
              resize: 'vertical',
              outline: 'none',
            }}
          />
        </div>

        {submitError && (
          <p style={{ color: '#dc2626', fontSize: 14, marginBottom: 12 }}>{submitError}</p>
        )}

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={score == null || submitting}
          style={{
            width: '100%',
            background: score == null ? 'var(--line)' : 'var(--accent)',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '14px',
            fontSize: 15,
            fontWeight: 600,
            cursor: score == null || submitting ? 'not-allowed' : 'pointer',
            transition: 'background 0.2s',
          }}
        >
          {submitting ? t('publicRating.submitting') : t('publicRating.submit')}
        </button>
      </div>
    </div>
  );
}
